import React, { useState } from "react";
import { ImageDown, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * One-click "Strongest Link" share card.
 *
 * Hits the backend /api/og/strongest/{kind}/{ref}.png endpoint and pushes the
 * resulting 1080x1080 PNG straight to the clipboard (rich-text + image-aware
 * targets). Falls back to triggering a download for browsers that don't allow
 * image clipboard writes.
 *
 * Universal across all three Vault categories:
 *   - kind="dossier",     ref=slug
 *   - kind="ai_dossier",  ref=id
 *   - kind="connection",  ref=id
 */
export default function StrongestLinkShareButton({
  kind,
  ref: artifactRef,
  testid = "strongest-share",
  size = "sm",
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onClick = async () => {
    if (!kind || !artifactRef) return;
    setBusy(true);
    try {
      const url = `${API}/og/strongest/${kind}/${encodeURIComponent(artifactRef)}.png?ts=${Date.now()}`;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error("card fetch failed");
      const blob = await res.blob();

      // Try clipboard first (works in modern Chrome / Safari / Edge).
      let copied = false;
      if (navigator.clipboard && window.ClipboardItem && window.isSecureContext) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          copied = true;
        } catch { /* fall through to download */ }
      }
      if (copied) {
        toast.success("Share card copied to clipboard");
      } else {
        // Fall back to download
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `vjones-${kind}-${artifactRef.slice(0, 8)}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
        toast.success("Share card downloaded");
      }
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch (e) {
      toast.error("Couldn't generate share card");
    } finally {
      setBusy(false);
    }
  };

  const cls = "inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] transition-colors disabled:opacity-50 border " +
    (size === "sm" ? "px-3 py-1.5 text-[10px] " : "px-4 py-2 text-[11px] ") +
    (done ? "border-[#00ff88]/60 text-[#00ff88] bg-[#00ff88]/5"
          : "border-[#ec4899]/40 text-[#ec4899] hover:bg-[#ec4899]/10");

  return (
    <button type="button" onClick={onClick} disabled={busy}
      data-testid={testid}
      title="Copy the Strongest-Link share card (1080×1080 PNG) to clipboard"
      className={cls}>
      {busy ? <Loader2 size={11} className="animate-spin" />
        : done ? <Check size={11} />
        : <ImageDown size={11} />}
      {busy ? "RENDERING…" : done ? "COPIED" : "SHARE CARD"}
    </button>
  );
}
